import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../hooks/ThemeContext";
import { useDineCentralAuth } from "../../hooks/useDineCentralAuth";
import { getRemoteEnv } from "../../config/remoteEnv";

// /link?store=…&order=CODE&c=<check id> — reached by scanning the QR on a
// till's customer display. The customer's email is tied to that one order;
// points are added only when the order completes, by the same loyalty path
// a till-entered email takes. The brand's theme wraps this page like any
// other. The check id is the key: without a valid one this page does nothing.
// &reward=<name> is added by the Rewards page's in-site scanner ("Redeem in
// store") — the till QR never carries it; it only changes the wording.
const API_BASE = process.env.REACT_APP_LOYALTY_API_BASE || "https://api.dineplatform.com";
const ID_OK = /^[a-f0-9]{16,64}$/i;
const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LinkPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useDineCentralAuth();
  const env = getRemoteEnv();

  const orderCode = (params.get("order") || "").toUpperCase().slice(0, 16);
  const checkId = (params.get("c") || "").trim();
  const storeId = (params.get("store") || "").trim();
  const reward = (params.get("reward") || "").trim().slice(0, 120);
  const valid = ID_OK.test(checkId) && !!storeId;

  const [email, setEmail] = useState("");
  // Same regime as checkout (PaymentDetailsComponent): UK = soft opt-out
  // ("tick if you'd rather not"), US = notice only, elsewhere = explicit opt-in.
  const regime = ((): "us" | "uk" | "strict" => {
    const c = (env.countryCode || "").trim().toUpperCase();
    if (c === "US" || c === "USA" || c === "UNITED STATES") return "us";
    if (c === "GB" || c === "UK" || c === "UNITED KINGDOM") return "uk";
    return "strict";
  })();
  const [optIn, setOptIn] = useState(false);       // strict regime
  const [refused, setRefused] = useState(false);   // uk regime
  const marketing = regime === "strict" ? optIn : !refused;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<null | { status: string; earned: boolean; email: string }>(null);
  const [dead, setDead] = useState<null | "expired" | "linked">(null);

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const accent = env.themeOverrideAccentColor || "#2563eb";
  const text = theme?.textColor || "inherit";
  // Paint our own background like the rewards page does — the body may be
  // dark (or light) independently of the brand theme's text colour.
  const page: React.CSSProperties = { minHeight: "100%", flex: 1, backgroundColor: theme?.backgroundColor || "transparent", color: text };
  const muted = { opacity: 0.72, fontSize: 14, lineHeight: 1.5 } as React.CSSProperties;
  const check: React.CSSProperties = { display: "flex", gap: 10, alignItems: "flex-start", textAlign: "left", marginTop: 8, fontSize: 13.5, lineHeight: 1.4, cursor: "pointer" };
  const box: React.CSSProperties = { maxWidth: 440, margin: "0 auto", padding: "28px 20px 40px", color: text, textAlign: "center" };
  const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", fontSize: 17, padding: "13px 14px", borderRadius: 10, border: "1px solid rgba(128,128,128,0.4)", background: "transparent", color: text, marginTop: 14 };
  const button: React.CSSProperties = { width: "100%", marginTop: 14, padding: "14px 18px", borderRadius: 10, border: 0, background: accent, color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer" };
  const ghost: React.CSSProperties = { ...button, background: "transparent", color: text, border: "1px solid rgba(128,128,128,0.4)" };
  const askStaff = reward ? <p style={{ ...muted, marginTop: 12, fontSize: 15, opacity: 1 }}>Now tell staff you'd like to redeem <strong>{reward}</strong> — your points will show on their till.</p> : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!EMAIL_OK.test(em)) { setError("Please enter a valid email address."); return; }
    setBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/loyalty/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appId: env.appId, storeId, orderId: checkId, orderCode, email: em, marketingOptIn: marketing }),
      });
      const d = await res.json().catch(() => ({}));
      if (d?.code === "expired" || d?.code === "linked") { setDead(d.code); return; }
      if (!res.ok || d?.ok !== true) { setError(d?.error || "Something went wrong — please try again."); return; }
      setDone({ status: d.status, earned: d.earned === true, email: em });
    } catch {
      setError("No connection — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!valid) {
    return (
      <div style={page}><div style={box}>
        <h2 style={{ marginBottom: 8 }}>This link isn't valid</h2>
        <p style={muted}>Scan the code on the screen at the till again, or ask a member of staff to add your email.</p>
      </div></div>
    );
  }

  if (dead) {
    return (
      <div style={page}><div style={box}>
        <h2 style={{ marginBottom: 8 }}>{dead === "linked" ? "This order already has an email" : "This order is already complete"}</h2>
        <p style={muted}>
          {dead === "linked"
            ? "An email was added at the till, so today's points are already on their way to that account."
            : "The code on the till screen works while you're paying. Next time, scan it before the order is completed and today's points will go to your account."}
        </p>
        {dead === "linked" && askStaff}
        <button style={button} onClick={() => navigate("/rewards")}>View my rewards</button>
      </div></div>
    );
  }

  if (done) {
    return (
      <div style={page}><div style={box}>
        <div style={{ fontSize: 44, marginBottom: 6 }}>✓</div>
        <h2 style={{ marginBottom: 8 }}>You're linked{orderCode ? ` to order #${orderCode}` : ""}</h2>
        <p style={muted}>
          {done.earned
            ? <>Your points for this order have been added to <strong>{done.email}</strong>.</>
            : <>Points for this order will be added to <strong>{done.email}</strong> as soon as it's completed.</>}
        </p>
        {askStaff}
        <button style={button} onClick={() => navigate("/rewards")}>View my rewards</button>
        <p style={{ ...muted, marginTop: 10, fontSize: 12.5 }}>You may be asked to sign in with that email.</p>
      </div></div>
    );
  }

  return (
    <div style={page}><form style={box} onSubmit={submit}>
      <h2 style={{ marginBottom: 6 }}>{reward ? `Redeem ${reward}` : `Collect points${orderCode ? ` on order #${orderCode}` : ""}`}</h2>
      <p style={muted}>
        {reward
          ? <>Link your email to this order{orderCode ? ` (#${orderCode})` : ""} so staff can see your points at the till. Nothing is charged, and today's points go to your account too.</>
          : <>Add your email and today's points from this order go to your {env.appName || "loyalty"} account. Nothing is charged; this only links the order to you.</>}
      </p>
      <input style={input} type="email" inputMode="email" autoComplete="email" autoFocus placeholder="you@example.com"
        value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
      {regime === "strict" && (
        <label style={check}>
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} disabled={busy} style={{ marginTop: 3 }} />
          <span>Also send me {env.appName ? `${env.appName}'s` : "the"} offers and seasonal menus by email (unsubscribe any time).</span>
        </label>
      )}
      {regime === "uk" && (
        <>
          <p style={{ ...muted, fontSize: 12.5, marginTop: 12 }}>We'll email you {env.appName || "our"} offers and seasonal menus — unsubscribe anytime.</p>
          <label style={check}>
            <input type="checkbox" checked={refused} onChange={(e) => setRefused(e.target.checked)} disabled={busy} style={{ marginTop: 3 }} />
            <span>Tick here if you'd rather not receive these emails</span>
          </label>
        </>
      )}
      {regime === "us" && (
        <p style={{ ...muted, fontSize: 12.5, marginTop: 12 }}>By linking your email, you'll receive {env.appName || "our"} offers and seasonal menus by email. Unsubscribe anytime.</p>
      )}
      {error && <p style={{ color: "#dc2626", fontSize: 14, marginTop: 10 }}>{error}</p>}
      <button style={button} type="submit" disabled={busy || !email.trim()}>{busy ? "Linking…" : "Link my email"}</button>
      <button style={ghost} type="button" onClick={() => navigate("/")}>Not now</button>
    </form></div>
  );
};

export default LinkPage;
